import React from 'react';

import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {PipelineStepStatusCode, PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isStepConfigEditorModalSupported} from 'pipelines/generalized-step-config-editor/StepConfigEditorModal';
import Float from 'ui-library/floating-ui/Float';

import {DEFAULT_FLOAT_PROPS} from './PopoverConstants';
import TableCreationPopoverContents, {
  TABLE_CREATION_POPOVER_SUPPORTED_STEP_TYPES,
} from './TableCreationPopoverContents';
import TransformPopoverContents from './TransformPopoverContents';
import VisualizationPopoverContents from './VisualizationPopoverContents';

function getStepPopoverContentsRenderFn(step: GetAnalysisPipelineStep): (() => JSX.Element) | null {
  const {stepType, stepVersion} = step.definition;

  // Defensively, don't try to render content for steps with obviously incomplete input
  if (!step.inputStepDatas.every(({dataFrames}) => dataFrames.length > 0) || step.config == null) {
    return null;
  }

  if (isStepConfigEditorModalSupported(stepType, stepVersion)) {
    return () => <TransformPopoverContents step={step} />;
  }

  if (TABLE_CREATION_POPOVER_SUPPORTED_STEP_TYPES.has(stepType)) {
    return () => <TableCreationPopoverContents step={step} />;
  }

  if (stepType === PipelineStepType.VISUALIZATION) {
    // It's possible to render a FAILED visualization step with its cached input, but that could be misleading.
    return step.statusCode !== PipelineStepStatusCode.SUCCEEDED
      ? null
      : () => <VisualizationPopoverContents step={step} />;
  }

  return null;
}

interface StepPopoverProps {
  step: GetAnalysisPipelineStep;
  children: JSX.Element;
}

/**
 * Show a popover with details about a step.
 *
 * Popover contents are different for each step type, and not all step types have content.
 *
 * Popover contents should be read-only.
 */
export default function StepPopover({step, children}: StepPopoverProps): JSX.Element {
  const renderContentsFn = getStepPopoverContentsRenderFn(step);

  if (renderContentsFn == null) {
    return <div className="u-cursor-default">{children}</div>;
  }

  return (
    <Float {...DEFAULT_FLOAT_PROPS} renderContent={renderContentsFn}>
      <div className="u-pointer">{children}</div>
    </Float>
  );
}
